import { ChevronDown, Plus, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useDocumentVariables } from '../api'
import { SidePanelSection } from './SidePanelSection'

type Props = { onInsertVariable: (key: string) => void }

export function VariableLibrary({ onInsertVariable }: Props) {
  const { t } = useTranslation()
  const variables = useDocumentVariables().data ?? []
  const groups = useMemo(() => [...new Set(variables.map((item) => item.group))], [variables])
  const [q, setQ] = useState('')
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ temel: true })
  const searching = q.trim().length > 0

  const filtered = useMemo(() => {
    const n = q.trim().toLocaleLowerCase('tr')
    return variables.filter(
      (v) => !n || v.key.toLocaleLowerCase('tr').includes(n) || v.label.toLocaleLowerCase('tr').includes(n),
    )
  }, [q, variables])

  return (
    <SidePanelSection title={t('hr.belgeler.right.vars')}>
      <p className="text-xs leading-relaxed text-muted-foreground">{t('hr.belgeler.right.varsHint')}</p>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input className="h-9 pl-8 text-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('hr.belgeler.right.searchVar')} />
      </div>
      <div className="space-y-1.5">
        {groups.map((g) => {
          const items = filtered.filter((v) => v.group === g)
          if (!items.length) return null
          const open = searching || (openGroups[g] ?? false)
          return (
            <div key={g} className="overflow-hidden rounded-lg border bg-card">
              <button
                type="button"
                aria-expanded={open}
                className="flex w-full items-center gap-2 px-2.5 py-2 text-left text-xs font-semibold hover:bg-muted/40"
                onClick={() => setOpenGroups((s) => ({ ...s, [g]: !open }))}
              >
                <span className="min-w-0 flex-1 truncate">{t(`hr.belgeler.varGroups.${g}`)}</span>
                <span className="rounded-full bg-muted px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground">{items.length}</span>
                <ChevronDown className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
              </button>
              {open ? (
                <ul className="divide-y border-t">
                  {items.map((v) => (
                    <li key={v.key}>
                      <button
                        type="button"
                        className="group flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-primary/[0.04]"
                        onClick={() => onInsertVariable(v.key)}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-medium">{v.label}</span>
                          <span className="block truncate font-mono text-[10px] text-primary/80">{`{{${v.key}}}`}</span>
                        </span>
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-md border bg-card text-muted-foreground group-hover:border-primary/40 group-hover:text-primary">
                          <Plus className="size-3" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )
        })}
      </div>
    </SidePanelSection>
  )
}
